import { updateBlockAlign } from '@blocksuite/affine-block-note';
import { ImageBlockModel, TextAlign } from '@blocksuite/affine-model';
import { deleteSelectedModelsCommand } from '@blocksuite/affine-shared/commands';
import {
  ActionPlacement,
  blockCommentToolbarButton,
  edgelessToolbarI18nT,
  EditPropsStore,
  type ToolbarAction,
  type ToolbarActionGroup,
  type ToolbarModuleConfig,
  ToolbarModuleExtension,
} from '@blocksuite/affine-shared/services';
import {
  BookmarkIcon,
  CaptionIcon,
  CopyIcon,
  DeleteIcon,
  DownloadIcon,
  DuplicateIcon,
  TextAlignCenterIcon,
  TextAlignLeftIcon,
  TextAlignRightIcon,
} from '@blocksuite/icons/lit';
import { BlockFlavourIdentifier } from '@blocksuite/std';
import type { ExtensionType } from '@blocksuite/store';
import { computed } from '@preact/signals-core';
import { html } from 'lit';

import { ImageBlockComponent } from '../image-block';
import { ImageEdgelessBlockComponent } from '../image-edgeless-block';
import { convertImagePresentationXYWH } from '../image-presentation-geometry';
import { duplicate } from '../utils';

const trackBaseProps = {
  category: 'image',
  type: 'card view',
};

const imageViewDropdownMenu = {
  id: 'c.view',
  actions: [
    {
      id: 'image',
      get label() {
        return edgelessToolbarI18nT('toolbar.image');
      },
      run(ctx) {
        const model = ctx.getCurrentModelByType(ImageBlockModel);
        if (!model) return;
        ctx.store.updateBlock(model, {
          presentation: 'default',
          xywh: convertImagePresentationXYWH(
            model.xywh,
            model.props.presentation,
            'default'
          ),
        });
        ctx.std.get(EditPropsStore).recordLastProps('image', {
          presentation: 'default',
        });
      },
    },
    {
      id: 'polaroid',
      get label() {
        return edgelessToolbarI18nT('toolbar.polaroid');
      },
      run(ctx) {
        const model = ctx.getCurrentModelByType(ImageBlockModel);
        if (!model) return;
        ctx.store.updateBlock(model, {
          presentation: 'polaroid',
          xywh: convertImagePresentationXYWH(
            model.xywh,
            model.props.presentation,
            'polaroid'
          ),
        });
        ctx.std.get(EditPropsStore).recordLastProps('image', {
          presentation: 'polaroid',
        });
      },
    },
  ],
  content(ctx) {
    const model = ctx.getCurrentModelByType(ImageBlockModel);
    if (!model) return null;
    const viewType$ = computed(() =>
      model.props.presentation === 'polaroid'
        ? edgelessToolbarI18nT('toolbar.polaroid')
        : edgelessToolbarI18nT('toolbar.image')
    );
    return html`<affine-view-dropdown-menu
      .actions=${this.actions}
      .context=${ctx}
      .viewType$=${viewType$}
    ></affine-view-dropdown-menu>`;
  },
} as const satisfies ToolbarActionGroup<ToolbarAction>;

const builtinToolbarConfig = {
  actions: [
    {
      id: 'a.download',
      get tooltip() {
        return edgelessToolbarI18nT('toolbar.download');
      },
      icon: DownloadIcon(),
      run(ctx) {
        const block = ctx.getCurrentBlockByType(ImageBlockComponent);
        block?.download();
      },
    },
    {
      id: 'b.caption',
      get tooltip() {
        return edgelessToolbarI18nT('toolbar.caption');
      },
      icon: CaptionIcon(),
      run(ctx) {
        const block = ctx.getCurrentBlockByType(ImageBlockComponent);
        block?.captionEditor?.show();

        ctx.track('OpenedCaptionEditor', {
          ...trackBaseProps,
          control: 'add caption',
        });
      },
    },
    {
      id: 'c.1.align-left',
      get tooltip() {
        return edgelessToolbarI18nT('alignment.alignLeft');
      },
      icon: TextAlignLeftIcon(),
      run(ctx) {
        const block = ctx.getCurrentBlockByType(ImageBlockComponent);
        if (block) {
          ctx.chain
            .pipe(updateBlockAlign, {
              textAlign: TextAlign.Left,
              selectedBlocks: [block],
            })
            .run();
        }
      },
    },
    {
      id: 'c.2.align-center',
      get tooltip() {
        return edgelessToolbarI18nT('alignment.alignCenter');
      },
      icon: TextAlignCenterIcon(),
      run(ctx) {
        const block = ctx.getCurrentBlockByType(ImageBlockComponent);
        if (block) {
          ctx.chain
            .pipe(updateBlockAlign, {
              textAlign: TextAlign.Center,
              selectedBlocks: [block],
            })
            .run();
        }
      },
    },
    {
      id: 'c.3.align-right',
      get tooltip() {
        return edgelessToolbarI18nT('alignment.alignRight');
      },
      icon: TextAlignRightIcon(),
      run(ctx) {
        const block = ctx.getCurrentBlockByType(ImageBlockComponent);
        if (block) {
          ctx.chain
            .pipe(updateBlockAlign, {
              textAlign: TextAlign.Right,
              selectedBlocks: [block],
            })
            .run();
        }
      },
    },
    {
      id: 'd.comment',
      ...blockCommentToolbarButton,
      get tooltip() {
        return edgelessToolbarI18nT('toolbar.comment');
      },
    },
    {
      placement: ActionPlacement.More,
      id: 'a.clipboard',
      actions: [
        {
          id: 'a.copy',
          get label() {
            return edgelessToolbarI18nT('contextMenu.copy');
          },
          icon: CopyIcon(),
          run(ctx) {
            const block = ctx.getCurrentBlockByType(ImageBlockComponent);
            block?.copy();
          },
        },
        {
          id: 'b.duplicate',
          get label() {
            return edgelessToolbarI18nT('contextMenu.duplicate');
          },
          icon: DuplicateIcon(),
          run(ctx) {
            const block = ctx.getCurrentBlockByType(ImageBlockComponent);
            if (!block) return;

            duplicate(block);
          },
        },
      ],
    },
    {
      placement: ActionPlacement.More,
      id: 'b.conversions',
      actions: [
        {
          id: 'a.turn-into-card-view',
          get label() {
            return edgelessToolbarI18nT('contextMenu.turnIntoCardView');
          },
          icon: BookmarkIcon(),
          when(ctx) {
            const supported =
              ctx.store.schema.flavourSchemaMap.has('affine:attachment');
            if (!supported) return false;

            const block = ctx.getCurrentBlockByType(ImageBlockComponent);
            return Boolean(block?.blobUrl);
          },
          run(ctx) {
            const block = ctx.getCurrentBlockByType(ImageBlockComponent);
            block?.convertToCardView();
          },
        },
      ],
    },
    {
      placement: ActionPlacement.More,
      id: 'c.delete',
      get label() {
        return edgelessToolbarI18nT('contextMenu.delete');
      },
      icon: DeleteIcon(),
      variant: 'destructive',
      run(ctx) {
        const block = ctx.getCurrentBlockByType(ImageBlockComponent);
        if (!block) return;

        ctx.command.exec(deleteSelectedModelsCommand, {
          selectedModels: [block.model],
        });
      },
    },
  ],

  placement: 'inner',
} as const satisfies ToolbarModuleConfig;

const builtinSurfaceToolbarConfig = {
  actions: [
    imageViewDropdownMenu,
    {
      id: 'a.download',
      get tooltip() {
        return edgelessToolbarI18nT('toolbar.download');
      },
      icon: DownloadIcon(),
      run(ctx) {
        const block = ctx.getCurrentBlockByType(ImageEdgelessBlockComponent);
        block?.download();
      },
    },
    {
      id: 'b.caption',
      get tooltip() {
        return edgelessToolbarI18nT('toolbar.caption');
      },
      icon: CaptionIcon(),
      run(ctx) {
        const block = ctx.getCurrentBlockByType(ImageEdgelessBlockComponent);
        block?.captionEditor?.show();

        ctx.track('OpenedCaptionEditor', {
          ...trackBaseProps,
          control: 'add caption',
        });
      },
    },
  ],

  when: ctx => ctx.getSurfaceModelsByType(ImageBlockModel).length === 1,
} as const satisfies ToolbarModuleConfig;

export const createBuiltinToolbarConfigExtension = (
  flavour: string
): ExtensionType[] => {
  const name = flavour.split(':').pop();

  return [
    ToolbarModuleExtension({
      id: BlockFlavourIdentifier(flavour),
      config: builtinToolbarConfig,
    }),

    ToolbarModuleExtension({
      id: BlockFlavourIdentifier(`affine:surface:${name}`),
      config: builtinSurfaceToolbarConfig,
    }),
  ];
};
